<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RacePricing } from '@/types/events'
import { formatCentsAsCurrency } from '@/utils/currencyFormat'

const props = withDefaults(
  defineProps<{
    pricing: RacePricing
    variant?: 'compact' | 'full'
    /** Render only the spots-left line (the caller shows the price itself). */
    notesOnly?: boolean
    /** Leave the tier name pill out (the caller renders it elsewhere, e.g. RaceTierPill). */
    hideTierPill?: boolean
  }>(),
  { variant: 'compact', notesOnly: false, hideTierPill: false },
)

const { t, locale } = useI18n()

const priceText = computed<string>(() =>
  formatCentsAsCurrency(props.pricing.current_price_cents, props.pricing.currency, locale.value),
)

const tierName = computed<string | null>(() => props.pricing.current_tier?.name ?? null)

// Server value as is: already the lower of the tier's and the race's remaining spots.
const spotsText = computed<string | null>(() => {
  const spots = props.pricing.current_tier?.remaining_spots
  if (spots === null || spots === undefined) return null
  return t('public.race_price.spots_left', { count: spots }, spots)
})

// Crossed-out standard price, only when the current (tier) price is lower.
const standardPriceText = computed<string | null>(() =>
  props.pricing.current_price_cents < props.pricing.standard_price_cents
    ? formatCentsAsCurrency(props.pricing.standard_price_cents, props.pricing.currency, locale.value)
    : null,
)
</script>

<template>
  <div class="flex flex-col items-center gap-1" data-testid="race-price-display">
    <span v-if="!notesOnly" class="flex items-baseline justify-center gap-2">
      <s v-if="standardPriceText" class="text-sm text-slate-400" data-testid="race-price-standard">
        <span class="sr-only">{{ t('public.race_price.standard_price') }}</span>
        {{ standardPriceText }}
      </s>
      <span class="text-lg font-bold text-slate-900 text-center" data-testid="race-price-amount">{{ priceText }}</span>
    </span>
    <span
      v-if="tierName && !notesOnly && !hideTierPill"
      class="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700"
      data-testid="race-price-tier"
    >
      {{ tierName }}
    </span>
    <span
      v-if="variant === 'full' && notesOnly && spotsText"
      class="text-xs text-slate-500 text-center"
      data-testid="race-price-spots"
    >
      {{ spotsText }}
    </span>
  </div>
</template>
